<template>
    <modal
        ref="deliveryAddressModal"
        :adaptive="true"
        :class="{ 'mobile-item-modal': pivotY === 1 }"
        :clickToClose="true"
        :maxWidth="575"
        :pivotY="pivotY"
        :reset="true"
        :scrollable="true"
        height="auto"
        name="delivery-address-modal"
        transition="pop-out"
        width="575"
    >
        <div class="item-modal-content relative flex flex-col w-full">
            <div class="item-modal-body">
                <div class="item-modal-details flex-auto p-6 pb-1">
                    <div class="relative block w-full pb-3 sm:hidden">
                        <h4
                            class="font-heading text-xl font-bold text-subheading"
                        >
                            {{ $t('deliveryAddress.addDeliveryAddress') }}
                        </h4>
                        <div class="absolute right-0 top-0">
                            <button
                                aria-label="Hide delivery address"
                                class="text-primary transition duration-150 ease-in-out hover:text-gray-500 focus:text-gray-500 focus:outline-none"
                                type="button"
                                @click="close"
                            >
                                <close-icon />
                            </button>
                        </div>
                    </div>

                    <div>
                        <div v-if="hasAutocomplete" class="relative w-full">
                            <ideal-postcodes-autocomplete
                                v-if="hasIdealPostcodesAutocomplete"
                            />
                            <autocomplete v-else />
                        </div>
                        <div class="relative mb-4 mt-4">
                            <div class="relative w-full">
                                <input
                                    id="street"
                                    v-model="street"
                                    :class="{
                                        'ring ring-red-200 border-red-300':
                                            $v.street.$error,
                                        'shadow-sm': !$v.street.$error
                                    }"
                                    :disabled="disableAll"
                                    autocomplete="shipping address-line1"
                                    class="email-input block pt-3 px-4 w-full text-sm bg-secondary border rounded"
                                    name="street"
                                    required
                                    style="height: 50px"
                                    type="text"
                                />
                                <label
                                    class="email-input-label absolute pt-1 px-4 rounded pointer-events-none select-none"
                                    for="street"
                                >
                                    {{ $t('deliveryAddress.streetAddress') }}
                                </label>
                            </div>
                        </div>
                        <div class="relative mb-4 mt-4 w-full">
                            <input
                                id="buildingNumber"
                                ref="buildingNumber"
                                v-model="buildingNumber"
                                :class="{
                                    'ring ring-red-200 border-red-300':
                                        $v.buildingNumber.$error,
                                    'shadow-sm': !$v.buildingNumber.$error
                                }"
                                :disabled="disableAll"
                                autocomplete="shipping address-line2"
                                class="email-input block pt-3 px-4 w-full text-sm bg-secondary border rounded"
                                name="buildingNumber"
                                required
                                style="height: 50px"
                                type="text"
                            />
                            <label
                                class="email-input-label absolute pt-1 px-4 rounded pointer-events-none select-none"
                                for="buildingNumber"
                            >
                                {{ $t('deliveryAddress.buildingNumberOrName') }}
                            </label>
                        </div>

                        <div class="flex">
                            <div class="relative mb-4 w-full">
                                <input
                                    id="city"
                                    v-model="city"
                                    :class="{
                                        'ring ring-red-200 border-red-300':
                                            $v.city.$error,
                                        'shadow-sm': !$v.city.$error
                                    }"
                                    :disabled="disableAll"
                                    autocomplete="shipping locality"
                                    class="email-input block pt-3 px-4 w-full text-sm bg-secondary border rounded"
                                    name="email"
                                    required
                                    style="height: 50px"
                                    type="text"
                                />
                                <label
                                    class="email-input-label absolute pt-1 px-4 rounded pointer-events-none select-none"
                                    for="city"
                                >
                                    {{ $t('deliveryAddress.city') }}
                                </label>
                            </div>
                            <div class="relative mb-4 ml-2 w-full lg:w-2/5">
                                <input
                                    id="postCode"
                                    v-model="postCode"
                                    :class="{
                                        'ring ring-red-200 border-red-300':
                                            $v.postCode.$error,
                                        'shadow-sm': !$v.postCode.$error
                                    }"
                                    :disabled="disableAll"
                                    autocomplete="shipping postal-code"
                                    class="email-input block pt-3 px-4 w-full text-sm bg-secondary border rounded"
                                    name="postCode"
                                    required
                                    style="height: 50px"
                                    type="text"
                                />
                                <label
                                    class="email-input-label absolute pt-1 px-4 rounded pointer-events-none select-none"
                                    for="postCode"
                                >
                                    {{ $t('deliveryAddress.postCode') }}
                                </label>
                            </div>
                        </div>

                        <div v-if="!notesDisabled" class="relative mb-4">
                            <textarea
                                id="notes"
                                v-model.trim="notes"
                                :class="{
                                    'ring ring-red-200 border-red-300':
                                        $v.notes.$error
                                }"
                                class="email-input block pt-7 px-4 w-full text-sm bg-secondary border rounded shadow-sm"
                                name="notes"
                                required
                                style="height: 100px"
                                type="text"
                            >
                            </textarea>
                            <label
                                class="email-input-label absolute pt-2 px-4 rounded pointer-events-none select-none"
                                for="notes"
                            >
                                {{ $t('deliveryAddress.notes') }}
                            </label>
                        </div>

                        <span
                            v-if="hasError"
                            class="block -mb-3 mt-3 text-red-800 text-xs font-bold"
                            v-html="errorMessage"
                        >
                        </span>
                    </div>
                </div>
                <div
                    class="item-modal-footer flex flex-col-reverse justify-center p-6 md:flex-row"
                >
                    <base-button
                        v-if="!isMobile"
                        :button-text="$t('deliveryAddressModal.close')"
                        :is-border-transparent="true"
                        class="p-3 px-10 sm:block md:w-auto"
                        size="sm"
                        variant="secondaryActions"
                        @clicked="close"
                    />

                    <button
                        :class="{ spinner: isLoading }"
                        :disabled="isLoading"
                        class="clip-path-button rounded-button flex-auto font-bold font-button select-none text-center no-highlight px-12 py-4 sm:mb-3 md:mb-0 md:ml-6 md:w-auto bg-button text-button border-button-primary shadow text-sm rounded-sm"
                        @click="update"
                    >
                        <span v-show="!isLoading">
                            {{ $t('deliveryAddressModal.confirm') }}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    </modal>
</template>

<script>
import isMobile from '@/helpers/isMobileDevice';
import CloseIcon from '@/components/icons/CloseIcon';
import { mapActions, mapGetters, mapState } from 'vuex';
import { maxLength, requiredIf } from 'vuelidate/lib/validators';
import Autocomplete from '@/components/formElements/Autocomplete';
import addressRequirements from '@/util/addressRequirements.json';
import { getDeliveryFee } from '@/services/delivery-fee.service';
import { FulfillmentMethodsEnum } from '@/enums/index.js';
import { findDeepInvalid } from '@/helpers/findDeepInvalid';
// import IdealPostcodesAutocomplete from "@/components/formElements/IdealPostcodesAutocomplete";

const IdealPostcodesAutocomplete = () =>
    import('@/components/formElements/IdealPostcodesAutocomplete');

export default {
    name: 'DeliveryAddressModal',
    components: {
        IdealPostcodesAutocomplete,
        Autocomplete,
        CloseIcon
    },
    data() {
        return {
            isEdit: true,
            disableAll: false,
            isLoading: false,
            hasError: false,
            errorMessage: null
        };
    },
    validations: {
        buildingNumber: {
            required: requiredIf(function () {
                return this.hasField('buildingNumber');
            }),
            maxLength: maxLength(100)
        },
        street: {
            required: requiredIf(function () {
                return !(!this.hasField('street') && this.hasAutocomplete);
            }),
            maxLength: maxLength(100)
        },
        city: {
            required: requiredIf(function () {
                return !(!this.hasField('city') && this.hasAutocomplete);
            }),
            maxLength: maxLength(100)
        },
        postCode: {
            required: requiredIf(function () {
                return this.hasField('postCode');
            }),
            maxLength: maxLength(100)
        },
        notes: {
            required: requiredIf(function () {
                return this.$growthbook.isOn('require_delivery_notes');
            }),
            maxLength: maxLength(200)
        }
    },
    watch: {},
    methods: {
        ...mapActions({
            setAddress: 'deliveryAddress/setAddress',
            setStreet: 'deliveryAddress/setStreet',
            setBuildingNumber: 'deliveryAddress/setBuildingNumber',
            setCity: 'deliveryAddress/setCity',
            setPostCode: 'deliveryAddress/setPostCode',
            setCountry: 'deliveryAddress/setCountry',
            setNotes: 'deliveryAddress/setNotes',
            setCoordinates: 'deliveryAddress/setCoordinates',
            setFormattedAddress: 'deliveryAddress/setFormattedAddress',
            setUserConfirmedCoordinates:
                'deliveryAddress/setUserConfirmedCoordinates',
            cartTotalWithDiscount: 'cart/cartTotalWithDiscount'
        }),
        animateError() {
            const field = findDeepInvalid(this.$v);

            const element = field && document.getElementById(field);
            element?.classList.add('wobble');
            element &&
                setTimeout(() => element?.classList.remove('wobble'), 1600);
        },
        async update() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                this.$nextTick(() => this.animateError());

                return false;
            }

            // Store the old delivery fee before updating
            const oldDeliveryFee = this.currentDeliveryFee;

            // Always update delivery fee (matching FulfillmentMethodModal logic)
            const success = await this.updateDeliveryFee();

            // Don't close modal if delivery fee update failed
            if (!success) {
                return false;
            }

            // Check if delivery fee changed and show notification
            const newDeliveryFee = this.$store.state.cart.deliveryFee;
            if (oldDeliveryFee !== newDeliveryFee) {
                const oldFeeFormatted = `£${(oldDeliveryFee / 100).toFixed(2)}`;
                const newFeeFormatted = `£${(newDeliveryFee / 100).toFixed(2)}`;

                this.$notify({
                    group: 'PiggyRewards',
                    title: this.$t('deliveryAddressModal.deliveryFeeUpdated', {
                        oldFee: oldFeeFormatted,
                        newFee: newFeeFormatted
                    })
                });
            }

            this.close();
        },
        async updateDeliveryFee() {
            this.isLoading = true;
            this.hasError = false;
            this.errorMessage = null;

            const venuePostcode = this.venue.address?.postCode;

            // Determine if we have valid coordinates to use
            const hasValidCoordinates = !!(
                this.deliveryAddress.coordinates &&
                this.deliveryAddress.coordinates.lat &&
                this.deliveryAddress.coordinates.lng
            );

            let params = {
                origins: `${this.venue.address.street}, ${this.venue.address.city}, ${venuePostcode}, ${this.venue.address.country}`,
                destinations: hasValidCoordinates
                    ? `${this.deliveryAddress.coordinates.lat},${this.deliveryAddress.coordinates.lng}`
                    : `${this.street}, ${this.city}, ${this.postCode}, ${this.country}`,
                units: 'imperial',
                departure_time: 'now',
                hasAutocomplete: hasValidCoordinates,
                postCode: this.postCode,
                country: this.country
            };

            try {
                const { data } = await getDeliveryFee(this.venueId, params);

                if (!data || data === 'ZERO_RESULTS') {
                    this.hasError = true;
                    this.errorMessage = this.$t(
                        'fulfillmentMethodModal.errors.outOfRange'
                    );
                    return false;
                }

                // Update delivery fee and related data in store (matching geoDistanceV2)
                this.$store.commit(
                    'cart/setDeliveryTime',
                    parseInt(data.duration, 10)
                );
                this.$store.commit(
                    'cart/setDeliveryFeeFromZone',
                    data.deliveryFee
                );
                this.$store.commit(
                    'cart/SET_ZONE_DELIVERY_MIN_COST',
                    data.minDeliveryAmount
                );

                // Recalculate cart total with new delivery fee
                await this.cartTotalWithDiscount();

                return true;
            } catch (error) {
                console.error('Failed to update delivery fee:', error);
                this.hasError = true;
                this.errorMessage = this.$t(
                    'fulfillmentMethodModal.errors.outOfRange'
                );
                return false;
            } finally {
                this.isLoading = false;
            }
        },
        close() {
            this.$modal.hide('delivery-address-modal');
        },
        hasField(fieldName) {
            const country = this.deliveryAddress.country;
            const countryFields =
                addressRequirements.countries[country]?.fields;

            if (!countryFields) {
                return addressRequirements.default.fields.includes(fieldName);
            }

            return countryFields.includes(fieldName);
        }
    },
    computed: {
        ...mapState('venue', {
            venueId: state => state.venue.id,
            venue: state => state.venue
        }),
        ...mapState('cart', {
            currentDeliveryFee: state => state.deliveryFee
        }),
        ...mapGetters({
            deliveryAddress: 'deliveryAddress/address',
            formattedAddress: 'deliveryAddress/formattedAddress'
        }),
        pivotY() {
            return isMobile() ? 1 : 0.5;
        },
        isMobile() {
            return isMobile();
        },
        coordinates: {
            get() {
                return this.deliveryAddress.coordinates;
            }
        },
        showMap() {
            return !!(this.coordinates.lat && this.coordinates.lng);
        },
        street: {
            get() {
                return this.deliveryAddress.street;
            },
            set(value) {
                this.setStreet(value);
                this.$v.street.$touch();
                // Clear coordinates when manually editing address to prevent desync
                this.setCoordinates({ lat: '', lng: '' });
                this.setFormattedAddress('');
                this.setUserConfirmedCoordinates(false);
            }
        },
        buildingNumber: {
            get() {
                return this.deliveryAddress.buildingNumber;
            },
            set(value) {
                this.setBuildingNumber(value);
                this.$v.buildingNumber.$touch();
                // Clear coordinates when manually editing address to prevent desync
                this.setCoordinates({ lat: '', lng: '' });
                this.setFormattedAddress('');
                this.setUserConfirmedCoordinates(false);
            }
        },
        city: {
            get() {
                return this.deliveryAddress.city;
            },
            set(value) {
                this.setCity(value);
                this.$v.city.$touch();
                // Clear coordinates when manually editing address to prevent desync
                this.setCoordinates({ lat: '', lng: '' });
                this.setFormattedAddress('');
                this.setUserConfirmedCoordinates(false);
            }
        },
        postCode: {
            get() {
                return this.deliveryAddress.postCode;
            },
            set(value) {
                this.setPostCode((value || '').toUpperCase());
                this.$v.postCode.$touch();
                // Clear coordinates when manually editing address to prevent desync
                this.setCoordinates({ lat: '', lng: '' });
                this.setFormattedAddress('');
                this.setUserConfirmedCoordinates(false);
            }
        },
        country: {
            get() {
                return this.deliveryAddress.country;
            },
            set(value) {
                this.setCountry(value);
            }
        },
        notes: {
            get() {
                return this.deliveryAddress.notes;
            },
            set(value) {
                this.setNotes(value);
                this.$v.notes.$touch();
            }
        },
        hasAutocomplete() {
            if (this.country !== 'GB') {
                return true;
            }
            return this.$growthbook.isOn('address_autocomplete');
        },
        hasIdealPostcodesAutocomplete() {
            return this.$growthbook.isOn('ideal_postcodes_autocomplete');
        },
        notesDisabled() {
            const manjarosIds = [
                '6259',
                '6536',
                '6825',
                '6828',
                '7309',
                '7427',
                '7540',
                '7559',
                '7700',
                '7828',
                '7978',
                '8143',
                '8276',
                '8288',
                '8289',
                '8319',
                '8327',
                '8328',
                '8537',
                '8700',
                '8941',
                '9027',
                // Jeezburger
                '2877',
                // Balti Night
                '9250'
            ];
            return manjarosIds.includes(this.venueId.toString());
        }
    }
};
</script>
